<template>
  <div :class="classes.option">
    <span :class="classes.competitorWrapper">
      <WinCompetitor
        :eventId="eventId"
        :team="label"
        :class="classes.competitorName"
      />
    </span>
    <div ref="targetEl">
      <OddsButton
        :option="option"
        :class="classes.optionButton"
        @click="handleButtonClick"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ComputedRef, provide } from "vue";
import OddsButton from "./OddsButton.vue";
import WinCompetitor from "./WinCompetitor.vue";
import { createUseStyles } from "@fresh8/vue-jss/src";
import {
  injectBrand,
  ButtonStyle
} from "@fresh8/ad-data-composition/src/brand";
import { injectCardNumber, useAnalytics } from "../hooks/useAnalytics";
import { composeBetOption } from "@fresh8/ad-data-composition/src/betData";
import { composePopoverForm } from "@fresh8/ad-data-composition/src/popoverForm";
import { getTranslation } from "@fresh8/ad-data-composition/src/lang";
import { Option } from "@fresh8/types/src/betData";

const useStyles = createUseStyles<
  "option" | "competitorWrapper" | "competitorName" | "optionButton",
  {
    buttonStyle: ButtonStyle;
  }
>({
  option: {
    marginRight: "4px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    "&:last-of-type": {
      marginRight: 0
    }
  },
  competitorWrapper: {
    flex: 1,
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderTop: props =>
      `1px solid ${props.adTemplateBranding.marketBorderColor}`,
    borderLeft: props =>
      `1px solid ${props.adTemplateBranding.marketBorderColor}`,
    borderRight: props =>
      `1px solid ${props.adTemplateBranding.marketBorderColor}`,
    borderRadius: props =>
      `${props.buttonStyle.borderRadius}px ${props.buttonStyle.borderRadius}px 0 0`
  },
  competitorName: {
    fontSize: "8px"
  },
  optionButton: {
    flex: 1,
    borderTopRightRadius: "0 !important",
    borderTopLeftRadius: "0 !important"
  }
});

export default defineComponent({
  name: "WinOption",
  components: {
    OddsButton,
    WinCompetitor
  },
  props: {
    optionId: { type: String, required: true },
    eventId: { type: String, required: true },
    stagger: {
      type: Number,
      default: 0
    }
  },
  setup(props) {
    provide("button-transisiton-stagger", props.stagger);
    const {
      adTemplateBranding,
      brand,
      adTemplate,
      getButtonStyle
    } = injectBrand();

    const buttonId = computed(() => adTemplate.value.market?.oddsButton);

    const buttonStyle = computed(() => {
      return getButtonStyle(buttonId.value);
    });

    const styleComputed = computed(() => ({
      buttonStyle: buttonStyle.value,
      adTemplateBranding: adTemplateBranding.value
    }));

    const classes = useStyles(styleComputed);
    const option = composeBetOption(props.optionId) as ComputedRef<Option>;

    const cardNumber = injectCardNumber();
    const { handleClick } = useAnalytics(
      { ...props, cardNumber: cardNumber, marketId: option.value.marketID },
      "Odds Button"
    );

    const handleButtonClick = () => {
      if (option.value.suspended || !props.optionId) return;
      handleClick();
    };

    const { targetEl } = composePopoverForm("Market Button", [
      {
        type: "button",
        get: () => brand.value.adTemplate.market.oddsButton,
        set: (value: string) =>
          (brand.value.adTemplate.market.oddsButton = value)
      }
    ]);

    const label = computed(() =>
      option.value.label === "draw"
        ? getTranslation(window.navigator.language.toLowerCase()).ButtonTexts
            .Draw
        : option.value.label
    );

    return {
      label,
      classes,
      option,
      targetEl,
      handleButtonClick
    };
  }
});
</script>

<style></style>
